<template>
  <div class="chat-log-container d-flex flex-column">
    <div class="chat-log-list-wrapper">
      <div v-if="loadingChatLog" class="h-50 d-flex justify-center align-center">
        <v-progress-circular
          indeterminate
          color="grey"
          size="36"
          width="2"
        />
      </div>
      <div v-else-if="!!chatLog?.length" class="chat-log-list" @copy.stop>
        <div v-for="log in chatLog" :key="log.id" class="prompt" :class="{'user-prompt': isUserPrompt(log)}">
          <div v-if="isUserPrompt(log)" class="user-prompt-row">
            <div class="prompt-box user-prompt-box">
              <span class="chat-log-content">
                {{ log.content }}
              </span>
            </div>
            <v-btn
              v-if="!loadingResponse && log.workflowVersion"
              type="button"
              size="28"
              density="compact"
              variant="text"
              color="grey-darken-1"
              class="edit-btn"
              :disabled="loadingResponse || loadingChatLog"
              @click="startEditPromptFromLog(log)"
            >
              <v-icon size="16">mdi-pencil-outline</v-icon>
            </v-btn>
          </div>
          <div v-else class="prompt-box ai-response-box">
            <Markdown :source="log.content" class="markdown-content" />
          </div>
        </div>
        <div v-if="streamingMessage" class="prompt">
          <div class="prompt-box ai-response-box">
            <Markdown :source="streamingMessage" class="markdown-content" />
          </div>
        </div>
        <div v-if="loadingResponse && !streamingMessage" class="assistant-status">
          <dots-loader />
          <div v-if="assistantStatusText" class="assistant-status-text">
            {{ assistantStatusText }}
          </div>
        </div>
      </div>
      <div v-else class="prompt-box ai-response-box">
        <div class="markdown-content">
          Welcome to the AI Copilot. Type instructions or questions to explore and adjust your workflow.
        </div>
      </div>
      <div v-if="loadingResponse && !chatLog?.length" class="assistant-status">
        <dots-loader />
        <div v-if="assistantStatusText" class="assistant-status-text">
          {{ assistantStatusText }}
        </div>
      </div>
      <div ref="scrollTargetRef" />
    </div>
    <div>
      <div class="suggestion-chips">
        <span class="suggestion-label">Try:</span>
        <v-chip
          v-for="suggestion in suggestions"
          :key="suggestion.prompt"
          size="small"
          variant="tonal"
          color="primary"
          class="suggestion-chip"
          :prepend-icon="suggestion.icon"
          :disabled="loadingResponse || isEditingPrompt"
          @click="submitSuggestion(suggestion.prompt)"
        >
          {{ suggestion.label }}
        </v-chip>
      </div>
      <div v-if="isEditingPrompt" class="edit-mode-banner">
        <div class="edit-mode-title">
          Editing an earlier prompt (workflow v{{ editPromptContext?.workflowVersion }})
        </div>
        <div class="edit-mode-subtitle">
          Submitting will restore the workflow to that version and re-run the assistant from there.
        </div>
      </div>
      <v-form ref="chatLogForm" @submit.prevent="updateWorkflowWithAssistant">
        <div class="chat-log-form">
          <v-textarea
            :rules="[rules.required]"
            auto-grow
            rows="3"
            class="no-border"
            variant="outlined"
            v-model="aiDescription"
            ref="promptInputRef"
            placeholder="Prompt"
            @keydown.enter="onKeyDown"
            @keydown.esc="isEditingPrompt ? cancelEditPrompt() : null"
          />
          <div class="d-flex justify-end ga-2">
            <v-btn
              v-if="isEditingPrompt && !loadingResponse"
              type="button"
              size="small"
              variant="outlined"
              color="grey-darken-3"
              prepend-icon="mdi-close"
              @click="cancelEditPrompt"
              :disabled="loadingRequest"
            >
              Cancel
            </v-btn>
            <v-btn
              :type="loadingResponse ? 'button' : 'submit'"
              size="small"
              variant="outlined"
              :color="loadingResponse ? 'error' : 'primary'"
              :prepend-icon="loadingResponse ? 'mdi-stop' : 'mdi-send'"
              :loading="loadingRequest && !loadingResponse"
              :disabled="loadingRequest && !loadingResponse"
              @click="loadingResponse ? stopAssistant() : null"
            >
              {{ loadingResponse ? 'Stop' : (isEditingPrompt ? 'Revert & Re-run' : 'Submit') }}
            </v-btn>
          </div>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useStore } from 'vuex'
import Markdown from 'vue-markdown-render'
import DotsLoader from '@/components/DotsLoader.vue'
import chatLogApi from '@/api/chatLogApi'
import safeStructuredClone from '@/utils/clone'
import { chatLogType } from '@/ai/prompts/common/messagesHistory'
import { useWorkflowAssistantFunctions } from '@/composables/useWorkflowAssistantFunctions'
import { getUpdatesSummaryText, getUpdateSummary } from '@/api/assistant/helpers/updateSummary'
import { generateWorkflowAssistant, generateFunctionCallResponse, validateWorkflowAssistantAction } from '@/ai'

const props = defineProps({ currentUIState: Object })

const scrollTargetRef = ref(null)
const aiDescription = ref('')
const loadingResponse = ref(false)
const loadingRequest = ref(false)
const chatLogForm = ref(null)
const promptInputRef = ref(null)
const loadingChatLog = ref(false)
const rules = ref({ required: (value) => !!value || 'Required' })
const activeAbortController = ref(null)
const stopRequested = ref(false)
const assistantStatusText = ref('')
const streamingMessage = ref('')
const editPromptContext = ref(null)

const store = useStore()
const { callFunction } = useWorkflowAssistantFunctions()
const workflow = computed(() => store.state.workflow.workflow)
const showSidePanel = computed(() => store.state.showSidePanel)
const user = computed(() => store.state.user.user)
const chatLog = computed(() => ([...store.state.chatLog.chatLog ?? []]).reverse())
const cardTypes = computed(() => store.state.cardTypes.cardTypes)
const svgGraph = computed(() => store.getters['svgGraphData/svgGraphData'])
const svgLanes = computed(() => store.getters['svgGraphData/svgLanesData'])
const svgGroups = computed(() => store.getters['svgGraphData/svgGroupsData'])
const isEditingPrompt = computed(() => !!editPromptContext.value)

const suggestions = [
  { label: 'Regenerate Cards', prompt: 'Regenerate all cards on events', icon: 'mdi-auto-fix' },
  { label: 'Add Missing Cards', prompt: 'Add missing cards on events', icon: 'mdi-card-plus' },
  { label: 'Regenerate Command Fields', prompt: 'Regenerate all command fields on events', icon: 'mdi-database-refresh' },
  {
    label: 'Add Missing Command Fields',
    prompt: 'Add missing command fields to Command cards that do not have fields defined',
    icon: 'mdi-database-plus'
  },
  { label: 'Regenerate Read Model Fields', prompt: 'Regenerate all read model fields on events', icon: 'mdi-eye-refresh' },
  {
    label: 'Add Missing Read Model Fields',
    prompt: 'Add missing read model fields to ReadModel cards that do not have fields defined',
    icon: 'mdi-eye-plus'
  },
  { label: 'Regenerate Entities', prompt: 'Regenerate all entities from the workflow', icon: 'mdi-table-refresh' },
  { label: 'Add Missing Entities', prompt: 'Add missing entities from the workflow', icon: 'mdi-table-plus' }
]

const stopAssistant = () => {
  stopRequested.value = true
  assistantStatusText.value = 'Stopping…'
  if (activeAbortController.value) {
    activeAbortController.value.abort()
  }
}

const isUserPrompt = (log) => log?.type === chatLogType.USER_PROMPT || !!log?.userId

const startEditPromptFromLog = async (log) => {
  if (loadingResponse.value || loadingChatLog.value) {
    return
  }
  if (!log?.id || !log?.workflowVersion) {
    return
  }

  editPromptContext.value = {
    logId: log.id,
    workflowVersion: log.workflowVersion,
    originalPrompt: log?.content || ''
  }
  aiDescription.value = log?.content || ''
  await nextTick()
  promptInputRef.value?.focus?.()
}

const cancelEditPrompt = async () => {
  editPromptContext.value = null
  aiDescription.value = ''
  await nextTick()
  promptInputRef.value?.focus?.()
}

const getMessagesHistoryBeforeLogId = (messages, logId) => {
  if (!Array.isArray(messages) || !logId) {
    return Array.isArray(messages) ? messages : []
  }

  const idx = messages.findIndex((m) => m?.id === logId)
  if (idx === -1) {
    return messages
  }

  return messages.slice(0, idx)
}

const getChatLogTypeForReplay = (log) => {
  if (log?.type) {
    return log.type
  }
  return log?.userId ? chatLogType.USER_PROMPT : chatLogType.AI_TEXT_RESPONSE
}

const truncateChatLogUITo = (entriesChronological) => {
  const newestFirst = Array.isArray(entriesChronological)
    ? [...entriesChronological].reverse()
    : []
  store.commit('chatLog/setChatLog', newestFirst)
}

const persistTruncatedChatLogSilently = async (entriesChronological) => {
  if (!workflow.value?.projectId || !workflow.value?.id) {
    throw new Error('Missing workflow info for chat log replay')
  }

  // Suppress subscription + local commits while we rebuild server state.
  store.commit('chatLog/setSuppressIncoming', true)
  try {
    await chatLogApi.clearChatLog({
      projectId: workflow.value.projectId,
      workflowId: workflow.value.id,
      silent: true
    })

    if (!Array.isArray(entriesChronological) || entriesChronological.length === 0) {
      return
    }

    const rebuiltChronological = []
    for (const entry of entriesChronological) {
      const type = getChatLogTypeForReplay(entry)
      const content = entry?.content ?? ''

      const created = await chatLogApi.createChatLog({
        projectId: workflow.value.projectId,
        input: {
          workflowId: workflow.value.id,
          type,
          content,
          userId: entry?.userId,
          aiOutput: entry?.aiOutput,
          workflowVersion: entry?.workflowVersion
        },
        silent: true
      })

      if (created?.id) {
        rebuiltChronological.push(created)
      }
    }

    // Swap the local store to match server state (new ids) in one go to avoid occasional duplicates
    // from delayed subscription deliveries.
    const newestFirst = [...rebuiltChronological].reverse()
    store.commit('chatLog/setChatLog', newestFirst)
  } finally {
    store.commit('chatLog/setSuppressIncoming', false)
  }
}

const restoreWorkflowForEdit = async (restoreVersion) => {
  if (!restoreVersion || !workflow.value?.version) {
    return false
  }

  const beforeVersion = workflow.value.version
  await store.dispatch('workflow/restorePreviousWorkflowVersion', {
    restoreVersion,
    workflow: workflow.value
  })

  await nextTick()
  const after = store.state.workflow.workflow
  if (!after) {
    return false
  }

  // Prefer checking sameAsVersion when available; otherwise fall back to detecting a version change.
  if (after.sameAsVersion === restoreVersion) {
    return true
  }

  return after.version !== beforeVersion
}

const updateWorkflowWithAssistant = async () => {
  try {
    const validationResult = await chatLogForm.value.validate()
    if (!validationResult?.valid) {
      return
    }

    stopRequested.value = false
    assistantStatusText.value = 'Thinking…'
    const abortController = new AbortController()
    activeAbortController.value = abortController

    loadingRequest.value = true
    loadingResponse.value = true
    const editContextSnapshot = editPromptContext.value
    const prompt = aiDescription.value
    resetForm()

    const fullMessagesHistory = safeStructuredClone(chatLog.value)
    let messagesHistory = fullMessagesHistory
    if (editContextSnapshot?.logId) {
      messagesHistory = getMessagesHistoryBeforeLogId(fullMessagesHistory, editContextSnapshot.logId)
    }

    if (editContextSnapshot?.workflowVersion) {
      const restoreVersion = editContextSnapshot.workflowVersion
      const currentVersion = workflow.value?.version

      if (currentVersion && restoreVersion > currentVersion) {
        store.commit('snackbar/showMessage', {
          content: `Cannot restore to version ${restoreVersion}. Current version is ${currentVersion}.`,
          timeout: 5000,
          color: 'red',
          location: 'center center'
        }, { root: true })
        aiDescription.value = prompt
        await nextTick()
        promptInputRef.value?.focus?.()
        return
      }

      truncateChatLogUITo(messagesHistory)

      assistantStatusText.value = 'Restoring chat history…'
      await persistTruncatedChatLogSilently(messagesHistory)

      if (currentVersion && restoreVersion < currentVersion) {
        assistantStatusText.value = `Restoring workflow to version ${restoreVersion}…`
        const restored = await restoreWorkflowForEdit(restoreVersion)
        if (!restored) {
          aiDescription.value = prompt
          await nextTick()
          promptInputRef.value?.focus?.()
          return
        }
      }

      editPromptContext.value = null
    }

    const workflowVersionAtPrompt = workflow.value?.version ?? null

    const created = await chatLogApi.createChatLog({
      projectId: workflow.value.projectId,
      input: {
        workflowId: workflow.value.id,
        type: chatLogType.USER_PROMPT,
        content: prompt,
        userId: user.value?.id,
        workflowVersion: workflowVersionAtPrompt
      }
    })

    if (!created?.id) {
      store.commit('snackbar/showMessage', {
        content: 'Failed to save prompt. Please try again.',
        timeout: 6000,
        color: 'red',
        location: 'center center'
      }, { root: true })
      return
    }

    loadingRequest.value = false

    const response = await generateWorkflowAssistant({
      workflow: { ...workflow.value, cardTypes: cardTypes.value },
      messagesHistory,
      prompt,
      currentUIState: props.currentUIState,
      abortController
    })

    if (stopRequested.value || abortController.signal.aborted) {
      return
    }

    if (response?.error) {
      await chatLogApi.createChatLog({
        projectId: workflow.value.projectId,
        input: {
          workflowId: workflow.value.id,
          type: chatLogType.AI_TEXT_RESPONSE,
          content: `I couldn't get a response from the AI assistant.\n\n${response.error.name}: ${response.error.message}\n\nPlease try again.`,
          aiOutput: null
        }
      })
      return
    }

    if (!response?.output?.length) {
      await chatLogApi.createChatLog({
        projectId: workflow.value.projectId,
        input: {
          workflowId: workflow.value.id,
          type: chatLogType.AI_TEXT_RESPONSE,
          content: 'I could not generate a response this time (empty output). Please try again.',
          aiOutput: null
        }
      })
      return
    }

    const updatesSummary = []
    const validationErrors = []
    const functionResults = []

    for (const output of response.output) {
      if (stopRequested.value || abortController.signal.aborted) {
        break
      }

      if (output.type === 'message') {
        assistantStatusText.value = ''
        const outputText = output.content.find((item) => item.type === 'output_text')?.text
        if (!outputText) {
          continue
        }
        await chatLogApi.createChatLog({
          projectId: workflow.value.projectId,
          input: {
            workflowId: workflow.value.id,
            type: chatLogType.AI_TEXT_RESPONSE,
            content: outputText,
            aiOutput: JSON.stringify(response.output)
          }
        })
      }

      if (output.type === 'function_call') {
        const functionName = output.name
        let args = {}
        try {
          args = output.arguments ? JSON.parse(output.arguments) : {}
        } catch (parseError) {
          validationErrors.push({
            functionName,
            args: output.arguments,
            error: `Invalid arguments for ${functionName}`
          })
          continue
        }
        assistantStatusText.value = `Running: ${functionName}…`
        const additionalArguments = {
          workflow: workflow.value,
          svgGraph: safeStructuredClone(svgGraph.value),
          svgLanes: safeStructuredClone(svgLanes.value),
          svgGroups: safeStructuredClone(svgGroups.value),
          cardTypes: safeStructuredClone(cardTypes.value)
        }

        // Validate the action before executing
        const validationResult = validateWorkflowAssistantAction(functionName, args, additionalArguments)

        if (!validationResult.valid) {
          // Store validation error to show to user
          validationErrors.push({
            functionName,
            args,
            error: validationResult.error
          })
        } else {
          if (stopRequested.value || abortController.signal.aborted) {
            break
          }

          // Only execute if validation passed
          const result = await callFunction(functionName, args, {
            signal: abortController.signal,
            shouldStop: () => stopRequested.value,
            onProgress: (text) => { assistantStatusText.value = text }
          })

          if (!result || result.success === false) {
            validationErrors.push({
              functionName,
              args,
              error: result?.error || `Could not complete ${functionName}`
            })
          } else {
            functionResults.push({
              callId: output.call_id,
              functionName,
              args,
              result
            })

            const summary = getUpdateSummary(functionName, args, additionalArguments, result)
            if (summary) {
              updatesSummary.push(summary)
            }
          }
        }
      }
    }

    // If there were validation errors, show them to the user
    if (validationErrors.length > 0) {
      const errorMessages = validationErrors.map((err) => `**${err.error}**`).join('\n\n')

      await chatLogApi.createChatLog({
        projectId: workflow.value.projectId,
        input: {
          workflowId: workflow.value.id,
          type: chatLogType.AI_TEXT_RESPONSE,
          content: `I encountered some issues with your request:\n\n${errorMessages}\n\nPlease revise your request based on these constraints.`,
          aiOutput: null
        }
      })
    }

    if (!validationErrors.length && functionResults.length > 0) {
      let summaryContent = getUpdatesSummaryText(updatesSummary)

      try {
        assistantStatusText.value = 'Writing summary…'
        const aiText = await generateFunctionCallResponse({
          workflow: workflow.value,
          prompt,
          updatesSummary,
          onDelta: (text) => {
            assistantStatusText.value = ''
            streamingMessage.value = text
          },
          abortController
        })

        if (aiText && !stopRequested.value) {
          summaryContent = aiText
        }
      } catch (error) {
        // Abort is a normal outcome when the user clicks Stop.
        if (stopRequested.value || abortController.signal.aborted || error?.name === 'AbortError') {
          return
        }

        console.error('Error generating AI response, using fallback:', error)
      }

      await chatLogApi.createChatLog({
        projectId: workflow.value.projectId,
        input: {
          workflowId: workflow.value.id,
          type: chatLogType.AI_FUNCTION_CALL,
          content: summaryContent,
          aiOutput: JSON.stringify({
            output: response.output,
            functionResults: functionResults.map(({ callId, result }) => ({
              callId,
              result: result?.success === false
                ? { success: false, error: result.error }
                : { success: true }
            }))
          })
        }
      })

      streamingMessage.value = ''
    }
  } catch (e) {
    // Abort is a normal outcome when the user clicks Stop.
    if (stopRequested.value || activeAbortController.value?.signal?.aborted || e?.name === 'AbortError') {
      return
    }

    try {
      await chatLogApi.createChatLog({
        projectId: workflow.value.projectId,
        input: {
          workflowId: workflow.value.id,
          type: chatLogType.AI_TEXT_RESPONSE,
          content: `Something went wrong while running the AI assistant.\n\n${e?.name || 'Error'}: ${e?.message || String(e)}\n\nPlease try again.`,
          aiOutput: null
        }
      })
    } catch (logError) {
      console.error('Failed to write assistant error to chat log:', logError)
    }
  } finally {
    activeAbortController.value = null
    stopRequested.value = false
    assistantStatusText.value = ''
    streamingMessage.value = ''
    loadingResponse.value = false
  }
}

const submitSuggestion = async (prompt) => {
  if (isEditingPrompt.value) {
    return
  }
  aiDescription.value = prompt
  await nextTick()
  await updateWorkflowWithAssistant()
}

const onKeyDown = (event) => {
  if (event.shiftKey) {
    return
  }

  event.preventDefault()
  updateWorkflowWithAssistant()
}

const getChatLog = async (workflowId) => {
  loadingChatLog.value = true
  await chatLogApi.getChatLog({ workflowId })
  loadingChatLog.value = false
}

const resetForm = () => {
  aiDescription.value = ''
  nextTick(() => chatLogForm.value.resetValidation())
}

watch(() => workflow.value.id, () => {
  if (workflow.value.id) {
    getChatLog(workflow.value.id)
  }
}, { immediate: true })

watch([
  () => chatLog.value?.length,
  () => showSidePanel.value,
  () => loadingChatLog.value,
  () => streamingMessage.value
], async ([_, showPanel, loading]) => {
  if (scrollTargetRef.value && showPanel && !loading) {
    await nextTick()
    scrollTargetRef.value.scrollIntoView({ behavior: 'smooth' })
  }
}, { immediate: true })
</script>

<style scoped>
.chat-log-container {
  height: calc(100vh - 134px);
}

.chat-log-list-wrapper {
  flex-grow: 1;
  margin-bottom: 20px;
  padding-right: 12px;
  overflow-y: auto;
}

.chat-log-list {
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  padding-bottom: 16px;
}

.prompt {
  display: flex;
}

.user-prompt {
  justify-content: flex-end;
}

.prompt-box {
  width: fit-content;
  padding: 4px;
  transition: all 0.15s;

  &.user-prompt-box {
    margin-right: 2px;
    padding: 12px 16px;
    border-radius: 16px 16px 4px 16px;
    background: #eeeeee;
    box-shadow: 3px 3px 5px 0 rgba(0, 0, 0, 0.1);
  }

  [data-theme="dark"] &.user-prompt-box {
    background: var(--light-4);
  }

  &.ai-response-box {
    margin-left: 2px;
    padding: 0;
    border-radius: 16px;

    &:first-child {
      margin-top: 2px;
    }
  }
}

.chat-log-content {
  white-space: pre-line;
}

.user-prompt-row {
  position: relative;
}

.user-prompt-row :deep(.edit-btn.v-btn) {
  position: absolute;
  bottom: -32px;
  right: 8px;
  min-width: 0;
  height: 28px !important;
  padding: 0;
  border-radius: 8px;
  border: none;
  background: transparent;
  box-shadow: none;
  opacity: 0;
  transition: opacity 0.15s ease;
}

.user-prompt-row:hover :deep(.edit-btn.v-btn) {
  opacity: 1;
}

.edit-mode-banner {
  border: 1px solid #e0e0e0;
  background: var(--light-1);
  border-radius: 10px;
  padding: 10px 12px;
  margin-bottom: 10px;
}

.edit-mode-title {
  font-size: 12px;
  font-weight: 600;
  color: #4f5359;
}

.edit-mode-subtitle {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

.suggestion-chips {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.suggestion-label {
  font-size: 12px;
  color: #666;
}

.suggestion-chip {
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

.assistant-status {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  row-gap: 6px;
}

.assistant-status-text {
  font-size: 12px;
  color: #666;
  user-select: none;
}

.chat-log-form {
  padding: 8px;
  border: 1px solid #dddddd;
  border-radius: 4px;

  & .v-btn.v-btn--size-small.v-btn--variant-outlined {
    padding: 5px 12px !important;
  }
}

:deep(.v-textarea) {
  & .v-field {
    & .v-field__outline__start,
    & .v-field__outline__end {
      border: none
    }

    & .v-field__input {
      padding: 4px;
      mask-image: none;
    }

  }

  & .v-input__details {
    display: none;
  }
}

:deep(.markdown-content) {
  margin: 16px 0;
  line-height: 1.6;

  ul {
    list-style-type: disc;
    margin: 8px 0;
    padding-left: 20px;
  }

  ol {
    list-style-type: decimal;
    margin: 8px 0;
    padding-left: 20px;
  }

  li {
    margin: 4px 0;
  }

  h1, h2, h3, h4 {
    margin: 16px 0 8px;
    font-weight: 600;
  }

  pre {
    white-space: pre-wrap;
  }
}
</style>
